import React, { useEffect, useRef, useState } from "react";
import ReactToPrint, { useReactToPrint } from "react-to-print";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import {
  Box,
  CircularProgress,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { WHITE, PRIMARY_LIGHT_BLUE } from "../../colors";
import HeightBox from "../../components/HeightBox";
import * as Yup from "yup";
import { Form, useFormik } from "formik";
import { CustomerApplicationPrintOut } from "../../components/Printouts/CustomerApplicationPrintOut";
import CustomerService from "../../services/customerService";
import { getAge } from "../../util/calculations.utils";
import { useHistory } from "react-router-dom";
import FormEditService from "../../services/formEditService";
import { APPLICATION_FORM } from "../../constants/formTypes";
import FormStateLabel from "./FormStateLabel";
import { APPROVED, IN_DRAFT, NEW_FORM } from "../../constants/formStates";
import FormPrinter from "./prints/FormPrinter";
import Autocomplete from "@mui/material/Autocomplete";
import countryList from "react-select-country-list";
import { phoneValidator } from "../../util/validators";
import EmployeeProfileImage from "../../components/uploads/EmployeeProfileImage";
import { getAllCustomers } from "../../actions/customers";
import { useDispatch } from "react-redux";

const Container = styled(Paper)(({ theme }) => ({
  backgroundColor: "#F5F6F8",
  opacity:0.9,
  borderRadius: 10,
  paddingTop: 20,
  paddingLeft: 20,
  paddingBottom: 20,
  boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.05)",
}));
const CustomTypography = styled(Typography)(({ theme }) => ({
  fontSize: 18,
}));
const CustomTextField = styled(TextField)(({ theme }) => ({
  width: 240,
  backgroundColor: WHITE,
  outline: "none",
  borderRadius: 10,
  border: "0px solid #fff",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "0px solid #484850",
  },
  "&:hover": {
    border: "0px solid #484850",
  },
  "&:focus-within": {
    border: "0px solid #484850",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "0px solid #484850",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "0px solid #484850",
  },
}));
const CustomButton = styled(Button)(({ theme }) => ({
  paddingLeft: 10,
  backgroundColor: PRIMARY_LIGHT_BLUE,
  height: 42,
  minWidth: 155,
  color: WHITE,
  textTransform: "none",
  fontSize: 18,
  fontWeight: 700,
  borderRadius: 10,
}));
const ErrorMessage = styled(Typography)(({ theme }) => ({
  color: "red",
  fontSize: 13,
}));

const initialValues = {
  reg_number: "",
  full_name: "",
  full_name_sinhala: "",
  region_sinhala: "",
  village: "",
  district: "",
  secretarial: "",
  guardian_name_sinhala: "",
  dob: "",
  license: "",
  nic: "",
  gender: 1,
  nationality: "",
  region: "",
  placeOfBirth: "",
  town: "",
  passport: "",
  passport_issued: new Date().toISOString().slice(0, 10),
  address: "",
  des_country: "",
  height: "",
  weight: "",
  complexion: "",
  education: "",
  marital_Status: false,
  noOfChildren: 0,
  guardian_name: "",
  guardian_number: "",
  passport_place: "",
  passport_expired: new Date().toISOString().slice(0, 10),
  phone_mobile: "",
  phone_home: "",
  phone_other: "",
  experienceOneCountry: "",
  experienceOneServicePeriodStart: "",
  experienceOneServicePeriodEnd: "",
  experienceOneJob: "",
  experienceTwoCountry: "",
  experienceTwoServicePeriodStart: "",
  experienceTwoServicePeriodEnd: "",
  experienceTwoJob: "",
  experienceThreeCountry: "",
  experienceThreeServicePeriodStart: "",
  experienceThreeServicePeriodEnd: "",
  experienceThreeJob: "",
  english: 0,
  hindi: 0,
  tamil: 0,
  arabic: 0,
  otherLang: "",
  otherLangFl: 0,
  post_applied_for: "",
  contract_start: new Date().toISOString().slice(0, 10),
  contract_end: new Date().toISOString().slice(0, 10),
  salary: "",
  passport_size_image: "",
  full_size_image: "",
  driving: false,
  cleaning: false,
  sewing: false,
  cooking: false,
  baby_sitting: false,
  washing: false,
  prev_country: "",
  prev_contract_start: new Date().toISOString().slice(0, 10),
  prev_contract_end: new Date().toISOString().slice(0, 10),
};
export default function CustomerApplication(props) {
  const dispatch = useDispatch();
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  const countries = React.useMemo(() => countryList().getData(), []);

  const [formValues, setFormValues] = useState(initialValues);

  const [guardianNumberError, setGuardianNumberError] = useState("");
  const [telephoneNumberError, setTelephoneNumberError] = useState("");
  const [homePhoneNumberError, setHomePhoneNumberError] = useState("");
  const [otherPhoneNumberError, setOtherPhoneNumberError] = useState("");
  const [pageInfo, setPageInfo] = useState({
    isDraft: false,
    isCreated: false,
  });

  const [showImageVals, setShowImageVals] = useState({
    passportSize: "",
    fullSize: "",
  });

  let history = useHistory();
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const handleSubmit = async (values) => {
    if (formValues.guardian_number && formValues.guardian_number !== "") {
      if (!phoneValidator(formValues.guardian_number)) {
        setGuardianNumberError("Invalid Phone Number");
      } else {
        setGuardianNumberError("");
      }
    }

    if (formValues.phone_mobile && formValues.phone_mobile !== "") {
      if (!phoneValidator(formValues.phone_mobile)) {
        setTelephoneNumberError("Invalid phone number");
      } else {
        setTelephoneNumberError("");
      }
    }
    if (formValues.phone_home && formValues.phone_home !== "") {
      if (!phoneValidator(formValues.phone_home)) {
        setHomePhoneNumberError("Invalid phone number");
      } else {
        setHomePhoneNumberError("");
      }
    }
    if (formValues.phone_other && formValues.phone_other !== "") {
      if (!phoneValidator(formValues.phone_other)) {
        setOtherPhoneNumberError("Invalid phone number");
      } else {
        setOtherPhoneNumberError("");
      }
    }

    if (
      guardianNumberError === "" &&
      homePhoneNumberError === "" &&
      otherPhoneNumberError === "" &&
      telephoneNumberError === ""
    ) {
      const res = createCustomer(values);
    }
  };
  const createCustomer = async () => {
    setLoading(true);
    var bodyFormData = new FormData();
    bodyFormData.set("reg_number", formValues.reg_number);
    bodyFormData.set("full_name", formValues.full_name);
    bodyFormData.set("full_name_sinhala", formValues.full_name_sinhala);
    bodyFormData.set("region_sinhala", formValues.region_sinhala);
    bodyFormData.set("village", formValues.village);
    bodyFormData.set("district", formValues.district);
    bodyFormData.set("secretarial", formValues.secretarial);
    bodyFormData.set("guardian_name_sinhala", formValues.guardian_name_sinhala);
    bodyFormData.set("dob", formValues.dob);
    bodyFormData.set("license", formValues.license);
    bodyFormData.set("nic", formValues.nic);
    bodyFormData.set("gender", formValues.gender);
    bodyFormData.set("nationality", formValues.nationality);
    bodyFormData.set("region", formValues.region);
    bodyFormData.set("placeOfBirth", formValues.placeOfBirth);
    bodyFormData.set("town", formValues.town);
    bodyFormData.set("passport", formValues.passport);
    bodyFormData.set("passport_issued", formValues.passport_issued);
    bodyFormData.set("address", formValues.address);
    bodyFormData.set("des_country", formValues.des_country);
    bodyFormData.set("height", formValues.height);
    bodyFormData.set("weight", formValues.weight);
    bodyFormData.set("complexion", formValues.complexion);
    bodyFormData.set("education", formValues.education);
    bodyFormData.set("marital_Status", formValues.marital_Status);
    bodyFormData.set("noOfChildren", formValues.noOfChildren);
    bodyFormData.set("guardian_name", formValues.guardian_name);
    bodyFormData.set("guardian_number", formValues.guardian_number);
    bodyFormData.set("passport_place", formValues.passport_place);
    bodyFormData.set("passport_expired", formValues.passport_expired);
    bodyFormData.set("phone_mobile", formValues.phone_mobile);
    bodyFormData.set("phone_home", formValues.phone_home);
    bodyFormData.set("phone_other", formValues.phone_other);
    bodyFormData.set("experienceOneCountry", formValues.experienceOneCountry);
    bodyFormData.set(
      "experienceOneServicePeriodStart",
      formValues.experienceOneServicePeriodStart
    );
    bodyFormData.set(
      "experienceOneServicePeriodEnd",
      formValues.experienceOneServicePeriodEnd
    );
    bodyFormData.set("experienceOneJob", formValues.experienceOneJob);
    bodyFormData.set("experienceTwoCountry", formValues.experienceTwoCountry);
    bodyFormData.set(
      "experienceTwoServicePeriodStart",
      formValues.experienceTwoServicePeriodStart
    );
    bodyFormData.set(
      "experienceTwoServicePeriodEnd",
      formValues.experienceTwoServicePeriodEnd
    );
    bodyFormData.set("experienceTwoJob", formValues.experienceTwoJob);
    bodyFormData.set(
      "experienceThreeCountry",
      formValues.experienceThreeCountry
    );
    bodyFormData.set(
      "experienceThreeServicePeriodStart",
      formValues.experienceThreeServicePeriodStart
    );
    bodyFormData.set(
      "experienceThreeServicePeriodEnd",
      formValues.experienceThreeServicePeriodEnd
    );
    bodyFormData.set("experienceThreeJob", formValues.experienceThreeJob);
    bodyFormData.set("english", formValues.english);
    bodyFormData.set("hindi", formValues.hindi);
    bodyFormData.set("tamil", formValues.tamil);
    bodyFormData.set("arabic", formValues.arabic);
    bodyFormData.set("otherLang", formValues.otherLang);
    bodyFormData.set("otherLangFl", formValues.otherLangFl);
    bodyFormData.set("post_applied_for", formValues.post_applied_for);
    bodyFormData.set("contract_start", formValues.contract_start);
    bodyFormData.set("contract_end", formValues.contract_end);
    bodyFormData.set("salary", formValues.salary);
    bodyFormData.set("passport_size_image", formValues.passport_size_image);
    bodyFormData.set("full_size_image", formValues.full_size_image);
    bodyFormData.set("driving", formValues.driving);
    bodyFormData.set("cleaning", formValues.cleaning);
    bodyFormData.set("cooking", formValues.cooking);
    bodyFormData.set("sewing", formValues.sewing);
    bodyFormData.set("baby_sitting", formValues.baby_sitting);
    bodyFormData.set("washing", formValues.washing);
    bodyFormData.set("prev_country", formValues.prev_country);
    bodyFormData.set("prev_contract_start", formValues.prev_contract_start);
    bodyFormData.set("prev_contract_end", formValues.prev_contract_end);

    let res;
    try{
    if (typeof props.customer === "number") {
      res = await CustomerService.updateCustomerFormDraft(
        props.customer,
        bodyFormData
      );
    } else {
      res = await CustomerService.createCustomer(bodyFormData);
    }
  }catch{
    setLoading(false);
  }
    dispatch(getAllCustomers());
    
    if (res.status == 200) {
      props.customer === "NEW_CUSTOMER"
        ? alert("Successfully created the customer")
        : alert("Customer update form in draft");
      handlePrint();
      history.push("/customers");
      return true;
    } else {
      alert(res.message);
      return false;
    }
  };
  useEffect(async () => {
    setIsDataLoaded(true);
    let newPageInfo = {
      isDraft: false,
      isCreated: false,
    };

    const getCustomer = async () => {
      const res = await CustomerService.getCustomerById(props.customer);
      return res.data;
    };
    const getFormDetails = async () => {
      const res = await FormEditService.getFormEditByCustomerAnType(
        props.customer,
        { type: APPLICATION_FORM }
      );
      return res.data;
    };

    try {
      if (props.customer !== "NEW_CUSTOMER") {
        const { id, isDraft } = await getFormDetails();

        newPageInfo = {
          isDraft,
          isCreated: true,
        };
      }
    } catch (error) {
      setIsDataLoaded(false);
    }
    if (props.customer === "NEW_CUSTOMER") {
      setFormValues({
        reg_number: "",
        full_name: "",
        region_sinhala: "",
        village: "",
        district: "",
        secretarial: "",
        guardian_name_sinhala: "",
        dob: "",
        license: "",
        nic: "",
        gender: 1,
        nationality: "",
        region: "",
        placeOfBirth: "",
        town: "",
        passport: "",
        passport_issued: new Date().toISOString().slice(0, 10),
        address: "",
        des_country: "",
        height: "",
        weight: "",
        complexion: "",
        education: "",
        marital_Status: false,
        noOfChildren: 0,
        guardian_name: "",
        guardian_number: "",
        passport_place: "",
        passport_expired: new Date().toISOString().slice(0, 10),
        phone_mobile: "",
        phone_home: "",
        phone_other: "",
        des_country: "",
        experienceOneCountry: "",
        experienceOneServicePeriodStart: "",
        experienceOneServicePeriodEnd: "",
        experienceOneJob: "",
        experienceTwoCountry: "",
        experienceTwoServicePeriodStart: "",
        experienceTwoServicePeriodEnd: "",
        experienceTwoJob: "",
        experienceThreeCountry: "",
        experienceThreeServicePeriodStart: "",
        experienceThreeServicePeriodEnd: "",
        experienceThreeJob: "",
        english: 0,
        hindi: 0,
        tamil: 0,
        arabic: 0,
        otherLang: "",
        otherLangFl: 0,
        post_applied_for: "",
        contract_start: new Date().toISOString().slice(0, 10),
        contract_end: new Date().toISOString().slice(0, 10),
        salary: "",
        passport_size_image: "",
        full_size_image: "",
        driving: false,
        cleaning: false,
        sewing: false,
        cooking: false,
        baby_sitting: false,
        washing: false,
        prev_country: "",
        prev_contract_start: new Date().toISOString().slice(0, 10),
        prev_contract_end: new Date().toISOString().slice(0, 10),
      });
    } else {
      const {
        reg_number,
        full_name,
        full_name_sinhala,
        region_sinhala,
        village,
        district,
        secretarial,
        guardian_name_sinhala,
        dob,
        license,
        nic,
        gender,
        nationality,
        region,
        placeOfBirth,
        town,
        passport,
        passport_issued,
        address,
        des_country,
        height,
        weight,
        complexion,
        education,
        marital_Status,
        noOfChildren,
        guardian_name,
        guardian_number,
        passport_place,
        passport_expired,
        phone_mobile,
        phone_home,
        phone_other,
        experienceOneCountry,
        experienceOneServicePeriodStart,
        experienceOneServicePeriodEnd,
        experienceOneJob,
        experienceTwoCountry,
        experienceTwoServicePeriodStart,
        experienceTwoServicePeriodEnd,
        experienceTwoJob,
        experienceThreeCountry,
        experienceThreeServicePeriodStart,
        experienceThreeServicePeriodEnd,
        experienceThreeJob,
        english,
        hindi,
        tamil,
        arabic,
        otherLang,
        otherLangFl,
        post_applied_for,
        contract_start,
        contract_end,
        salary,
        passport_size_image,
        full_size_image,
        driving,
        cleaning,
        sewing,
        cooking,
        baby_sitting,
        washing,
        prev_country,
        prev_contract_start,
        prev_contract_end,
      } = await getFormDetails();

      setShowImageVals({
        passportSize: passport_size_image,
        fullSize: full_size_image,
      });

      setFormValues({
        ...formValues,
        reg_number,
        full_name,
        full_name_sinhala,
        region_sinhala,
        village,
        district,
        secretarial,
        guardian_name_sinhala,
        dob,
        license,
        nic,
        gender,
        nationality,
        region,
        placeOfBirth,
        town,
        passport,
        passport_issued,
        address,
        des_country,
        height,
        weight,
        complexion,
        education,
        marital_Status,
        noOfChildren,
        guardian_name,
        guardian_number,
        passport_place,
        passport_expired,
        phone_mobile,
        phone_home,
        phone_other,
        experienceOneCountry,
        experienceOneServicePeriodStart,
        experienceOneServicePeriodEnd,
        experienceOneJob,
        experienceTwoCountry,
        experienceTwoServicePeriodStart,
        experienceTwoServicePeriodEnd,
        experienceTwoJob,
        experienceThreeCountry,
        experienceThreeServicePeriodStart,
        experienceThreeServicePeriodEnd,
        experienceThreeJob,
        english,
        hindi,
        tamil,
        arabic,
        otherLang,
        otherLangFl,
        post_applied_for,
        contract_start,
        contract_end,
        salary,
        driving,
        cleaning,
        sewing,
        cooking,
        baby_sitting,
        washing,
        prev_country,
        prev_contract_start,
        prev_contract_end,
      });
    }
    setPageInfo({ ...newPageInfo });
    setIsDataLoaded(false);
  }, [props.customer]);

  const selectPassportSizePhoto = (file) => {
    if (file) {
      const objectUrl = window.URL.createObjectURL(file);
      setShowImageVals({
        ...showImageVals,
        passportSize: objectUrl,
      });
      setFormValues({
        ...formValues,
        passport_size_image: file,
      });
    }
  };
  const selectFullSizePhoto = (file) => {
    if (file) {
      const objectUrl = window.URL.createObjectURL(file);
      setShowImageVals({
        ...showImageVals,
        fullSize: objectUrl,
      });
      setFormValues({
        ...formValues,
        full_size_image: file,
      });
    }
  };
  const handleChange = (event) => {
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.checked,
    });
  };
  return (
    <Container>
      <FormStateLabel
        type={
          pageInfo.isDraft ? IN_DRAFT : pageInfo.isCreated ? APPROVED : NEW_FORM
        }
      />
      {isDataLoaded && (
        <Box
          sx={{
            width: "100%",
            height: "30vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}
      {!isDataLoaded && (
        <form>
          <Stack direction="row" spacing={2}>
            <Stack direction="column" spacing={1}>
              <HeightBox height={2} />
              <CustomTypography>Registration Number</CustomTypography>
              {/* {formik.touched.reg_number && formik.errors.reg_number && <HeightBox height={20} />} */}
              <HeightBox height={2} />
              <CustomTypography>Full Name(Sinhala)</CustomTypography>
              <HeightBox height={4} />
              <CustomTypography>Full Name</CustomTypography>
              {/* {formik.touched.full_name && formik.errors.full_name && <HeightBox height={20} />} */}
              <HeightBox height={4} />
              <CustomTypography>dob</CustomTypography>
              {/* {formik.touched.dob && formik.errors.dob && <HeightBox height={20} />} */}
              <HeightBox height={4} />
              {/* <CustomTypography>Age</CustomTypography>
                  {formik.touched.age && formik.errors.age && <HeightBox height={20} />} */}
              <HeightBox height={5} />
              <CustomTypography>License ID</CustomTypography>
              {/* {formik.touched.nic && formik.errors.nic && <HeightBox height={20} />} */}
              <HeightBox height={5} />
              <CustomTypography>National ID</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Gender</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Nationality</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Region</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Region(Sinhala)</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Village(Sinhala)</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>District(Sinhala)</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>
                Secreterial Division (Sinhala)
              </CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Place of Birth</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Living town</CustomTypography>

            </Stack>
            <Stack direction="column" spacing={1}>
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    reg_number: e.target.value,
                  })
                }
                value={formValues.reg_number}
              />
              {/* {formik.touched.reg_number && <ErrorMessage>{formik.errors.reg_number}</ErrorMessage>} */}
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    full_name_sinhala: e.target.value,
                  })
                }
                value={formValues.full_name_sinhala}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, full_name: e.target.value })
                }
                value={formValues.full_name}
              />
              {/* {formik.touched.full_name && <ErrorMessage>{formik.errors.full_name}</ErrorMessage>} */}
              <CustomTextField
                type="date"
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, dob: e.target.value })
                }
                value={formValues.dob}
              />
              {/* {formik.touched.dob && <ErrorMessage>{formik.errors.dob}</ErrorMessage>} */}
              {/* <CustomTextField disabled size="small" onChange={formik.handleChange("age")} />
                  {formik.touched.age && <ErrorMessage>{formik.errors.age}</ErrorMessage>} */}
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, license: e.target.value })
                }
                value={formValues.license}
              />
              {/* {formik.touched.license && <ErrorMessage>{formik.errors.license}</ErrorMessage>} */}
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, nic: e.target.value })
                }
                value={formValues.nic}
              />
              <HeightBox height={6} />
              <RadioGroup
                row
                value={formValues.gender}
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    gender: e.target.value,
                  })
                }
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
              >
                <FormControlLabel value={2} control={<Radio />} label="Male" />
                <FormControlLabel
                  value={1}
                  control={<Radio />}
                  label="Female"
                />
              </RadioGroup>
              {/* new 1 */}
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, nationality: e.target.value })
                }
                value={formValues.nationality}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, region: e.target.value })
                }
                value={formValues.region}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    region_sinhala: e.target.value,
                  })
                }
                value={formValues.region_sinhala}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, village: e.target.value })
                }
                value={formValues.village}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, district: e.target.value })
                }
                value={formValues.district}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, secretarial: e.target.value })
                }
                value={formValues.secretarial}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, placeOfBirth: e.target.value })
                }
                value={formValues.placeOfBirth}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, town: e.target.value })
                }
                value={formValues.town}
              />
              {/*new 1 end  */}
 
              {/* {formik.touched.nic && <ErrorMessage>{formik.errors.nic}</ErrorMessage>} */}
            </Stack>
            <div style={{ width: 100 }} />
            <Stack direction="column" spacing={1}>
              <HeightBox height={2} />
              <CustomTypography>Destination Country</CustomTypography>
              {/* {formik.touched.des_country && formik.errors.des_country && <HeightBox height={20} />} */}
              <HeightBox height={12} />
              <CustomTypography>Height</CustomTypography>
              {/* {formik.touched.height && formik.errors.height && <HeightBox height={20} />} */}
              <HeightBox height={12} />
              <CustomTypography>Weight</CustomTypography>
              <HeightBox height={12} />
              <CustomTypography>Complexion</CustomTypography>
              <HeightBox height={12} />
              <CustomTypography>Ed. Qualification</CustomTypography>
              <HeightBox height={12} />
              <CustomTypography>Status</CustomTypography>
              <HeightBox height={7} />
              <CustomTypography>No. Children</CustomTypography>
              {/* {formik.touched.marriedChildrens && formik.errors.marriedChildrens && <HeightBox height={20} />} */}
              <HeightBox height={6} />
              <CustomTypography>Guardian's Name</CustomTypography>
              {/* {formik.touched.guardian_name && formik.errors.guardian_name && <HeightBox height={20} />} */}
              <HeightBox height={6} />
              <CustomTypography>Guardian's Name(Sinhala)</CustomTypography>
              {/* {formik.touched.guardian_name && formik.errors.guardian_name && <HeightBox height={20} />} */}
              <HeightBox height={5} />
              <CustomTypography>Guardian's Number</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Passport Number</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Date of Issue</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Place of Issue</CustomTypography>
              <HeightBox height={5} />
              <CustomTypography>Date of Expiry</CustomTypography>

            </Stack>
            <Stack direction="column" spacing={1}>
              <Autocomplete
                disablePortal
                onChange={(event, value) => {
                  setFormValues({
                    ...formValues,
                    des_country: value?.label,
                  });
                }}
                value={formValues.des_country}
                id="combo-box-demo"
                options={countries}
                renderInput={(params) => <CustomTextField {...params} />}
              />
              {/* <CustomTextField
              size="small"
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  des_country: e.target.value,
                })
              }
              value={formValues.des_country}
            /> */}
              {/* {formik.touched.des_country && <ErrorMessage>{formik.errors.des_country}</ErrorMessage>} */}
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, height: e.target.value })
                }
                value={formValues.height}
              />
              {/* {formik.touched.height && <ErrorMessage>{formik.errors.height}</ErrorMessage>} */}
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, weight: e.target.value })
                }
                value={formValues.weight}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, complexion: e.target.value })
                }
                value={formValues.complexion}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, education: e.target.value })
                }
                value={formValues.education}
              />
              <HeightBox height={6} />
              <RadioGroup
                row
                value={formValues.marital_Status}
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    marital_Status: e.target.value,
                  })
                }
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
              >
                <FormControlLabel
                  value={true}
                  control={<Radio />}
                  label="Married"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio />}
                  label="Not Married"
                />
              </RadioGroup>
              <HeightBox height={6} />
              <CustomTextField
                size="small"
                type="number"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    noOfChildren: e.target.value,
                  })
                }
                value={formValues.noOfChildren}
              />
              {/* {formik.touched.marriedChildrens && <ErrorMessage>{formik.errors.marriedChildrens}</ErrorMessage>} */}
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    guardian_name_sinhala: e.target.value,
                  })
                }
                value={formValues.guardian_name_sinhala}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    guardian_name: e.target.value,
                  })
                }
                value={formValues.guardian_name}
              />

              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    guardian_number: e.target.value,
                  })
                }
                value={formValues.guardian_number}
              />
              {guardianNumberError !== "" && (
                <ErrorMessage>{guardianNumberError}</ErrorMessage>
              )}
                           <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, passport: e.target.value })
                }
                value={formValues.passport}
              />
              <CustomTextField
                type="date"
                size="small"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    passport_issued: e.target.value,
                  })
                }
                value={formValues.passport_issued}
              />
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    passport_place: e.target.value,
                  })
                }
                value={formValues.passport_place}
              />
              <CustomTextField
                type="date"
                size="small"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    passport_expired: e.target.value,
                  })
                }
                value={formValues.passport_expired}
              />
            </Stack>
          </Stack>
          <HeightBox height={10} />
          <Stack direction="row" spacing={14.5}>
            <Stack direction="column" spacing={1}>
              <HeightBox height={1} />
              <CustomTypography>Address (Sinhala )</CustomTypography>
            </Stack>
            <Stack direction="column" spacing={1}>
              <CustomTextField
                size="small"
                style={{ width: 800 }}
                onChange={(e) =>
                  setFormValues({ ...formValues, address: e.target.value })
                }
                value={formValues.address}
              />
              {/* {formik.touched.address && <ErrorMessage>{formik.errors.address}</ErrorMessage>} */}
            </Stack>
          </Stack>
          <HeightBox height={10} />
          <Stack direction="row" spacing={4}>
            <Stack direction="column" spacing={1}>
              <HeightBox height={1} />
              <CustomTypography>Telephone Number</CustomTypography>
            </Stack>
            <Stack direction="row" spacing={5}>
              <div>
                <CustomTextField
                  size="small"
                  placeholder="Mobile"
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      phone_mobile: e.target.value,
                    })
                  }
                  value={formValues.phone_mobile}
                />
                {telephoneNumberError !== "" && (
                  <ErrorMessage>{telephoneNumberError}</ErrorMessage>
                )}
              </div>
              <div>
                <CustomTextField
                  size="small"
                  placeholder="Home"
                  onChange={(e) =>
                    setFormValues({ ...formValues, phone_home: e.target.value })
                  }
                  value={formValues.phone_home}
                />
                {homePhoneNumberError !== "" && (
                  <ErrorMessage>{homePhoneNumberError}</ErrorMessage>
                )}
              </div>
              <div>
                <CustomTextField
                  size="small"
                  placeholder="Other"
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      phone_other: e.target.value,
                    })
                  }
                  value={formValues.phone_other}
                />
                {otherPhoneNumberError !== "" && (
                  <ErrorMessage>{otherPhoneNumberError}</ErrorMessage>
                )}
              </div>
            </Stack>
          </Stack>
          <HeightBox height={10} />
          <Stack direction="row" spacing={12}>
            <Stack direction="column" spacing={1}>
              <HeightBox height={1} />
              <CustomTypography fontWeight={600}>Experience</CustomTypography>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={12}>
            <Stack direction="row" spacing={5}>
              <Stack direction="column" spacing={1}>
                <HeightBox height={1} />
                <CustomTypography>Country</CustomTypography>
                <Autocomplete
                  disablePortal
                  onChange={(event, value) => {
                    setFormValues({
                      ...formValues,
                      experienceOneCountry: value?.label,
                    });
                  }}
                  id="combo-box-demo"
                  options={countries}
                  value={formValues.experienceOneCountry}
                  renderInput={(params) => <CustomTextField {...params} />}
                />

                {/* {formik.touched.experienceOneCountry && <ErrorMessage>{formik.errors.experienceOneCountry}</ErrorMessage>} */}
                <Autocomplete
                  disablePortal
                  onChange={(event, value) => {
                    setFormValues({
                      ...formValues,
                      experienceTwoCountry: value?.label,
                    });
                  }}
                  value={formValues.experienceTwoCountry}
                  id="combo-box-demo"
                  options={countries}
                  renderInput={(params) => <CustomTextField {...params} />}
                />
                {/* {formik.touched.experienceTwoCountry && <ErrorMessage>{formik.errors.experienceTwoCountry}</ErrorMessage>} */}
                <Autocomplete
                  disablePortal
                  onChange={(event, value) => {
                    setFormValues({
                      ...formValues,
                      experienceThreeCountry: value?.label,
                    });
                  }}
                  value={formValues.experienceThreeCountry}
                  id="combo-box-demo"
                  options={countries}
                  renderInput={(params) => <CustomTextField {...params} />}
                />
                {/* {formik.touched.experienceThreeCountry && ( */}
                {/* <ErrorMessage>{formik.errors.experienceThreeCountry}</ErrorMessage>
              )} */}
              </Stack>
              <Stack direction="column" spacing={1}>
                <HeightBox height={1} />
                <CustomTypography>
                  Service Period (From &#8594; To){" "}
                </CustomTypography>
                <HeightBox size={80} />
                <Stack direction="row" spacing={1}>
                  <CustomTextField
                    type="date"
                    size="small"
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        experienceOneServicePeriodStart: e.target.value,
                      })
                    }
                    value={formValues.experienceOneServicePeriodStart}
                  />
                  {/* {formik.touched.experienceOneServicePeriodStart && ( */}
                  {/* <ErrorMessage>{formik.errors.experienceOneServicePeriodStart}</ErrorMessage>
                )} */}
                  <CustomTextField
                    type="date"
                    size="small"
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        experienceOneServicePeriodEnd: e.target.value,
                      })
                    }
                    value={formValues.experienceOneServicePeriodEnd}
                  />
                  {/* {formik.touched.experienceOneServicePeriodEnd && ( */}
                  {/* <ErrorMessage>{formik.errors.experienceOneServicePeriodEnd}</ErrorMessage>
                )} */}
                </Stack>
                <HeightBox size={80} />
                <Stack direction="row" spacing={1}>
                  <CustomTextField
                    type="date"
                    size="small"
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        experienceTwoServicePeriodStart: e.target.value,
                      })
                    }
                    value={formValues.experienceTwoServicePeriodStart}
                  />
                  {/* {formik.touched.experienceTwoServicePeriodStart && ( */}
                  {/* <ErrorMessage>{formik.errors.experienceTwoServicePeriodStart}</ErrorMessage>
                )} */}
                  <CustomTextField
                    type="date"
                    size="small"
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        experienceTwoServicePeriodEnd: e.target.value,
                      })
                    }
                    value={formValues.experienceTwoServicePeriodEnd}
                  />
                  {/* {formik.touched.experienceTwoServicePeriodEnd && ( */}
                  {/* <ErrorMessage>{formik.errors.experienceTwoServicePeriodEnd}</ErrorMessage>
                )} */}
                </Stack>
                <HeightBox size={80} />
                <Stack direction="row" spacing={1}>
                  <CustomTextField
                    type="date"
                    size="small"
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        experienceThreeServicePeriodStart: e.target.value,
                      })
                    }
                    value={formValues.experienceThreeServicePeriodStart}
                  />
                  {/* {formik.touched.experienceThreeServicePeriodStart && ( */}
                  {/* <ErrorMessage>{formik.errors.experienceThreeServicePeriodStart}</ErrorMessage>
                )} */}
                  <CustomTextField
                    type="date"
                    size="small"
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        experienceThreeServicePeriodEnd: e.target.value,
                      })
                    }
                    value={formValues.experienceThreeServicePeriodEnd}
                  />
                  {/* {formik.touched.experienceThreeServicePeriodEnd && ( */}
                  {/* <ErrorMessage>{formik.errors.experienceThreeServicePeriodEnd}</ErrorMessage>
                )} */}
                </Stack>
              </Stack>
              <Stack direction="column" spacing={1}>
                <HeightBox height={1} />
                <CustomTypography>Job</CustomTypography>
                <HeightBox size={80} />
                <CustomTextField
                  size="small"
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      experienceOneJob: e.target.value,
                    })
                  }
                  value={formValues.experienceOneJob}
                />
                {/* {formik.touched.experienceOneJob && <ErrorMessage>{formik.errors.experienceOneJob}</ErrorMessage>} */}
                <HeightBox size={80} />
                <CustomTextField
                  size="small"
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      experienceTwoJob: e.target.value,
                    })
                  }
                  value={formValues.experienceTwoJob}
                />
                {/* {formik.touched.experienceTwoJob && <ErrorMessage>{formik.errors.experienceTwoJob}</ErrorMessage>} */}
                <HeightBox size={80} />
                <CustomTextField
                  size="small"
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      experienceThreeJob: e.target.value,
                    })
                  }
                  value={formValues.experienceThreeJob}
                />
                {/* {formik.touched.experienceThreeJob && <ErrorMessage>{formik.errors.experienceThreeJob}</ErrorMessage>} */}
              </Stack>
            </Stack>
          </Stack>
          <HeightBox height={10} />
          {/* Prev exp */}
          <Stack direction="row" spacing={2}>
            <Stack direction="column" spacing={1}>
              <HeightBox height={2} />
              <CustomTypography>Country</CustomTypography>
              {/* {formik.touched.des_country && formik.errors.des_country && <HeightBox height={20} />} */}
              <HeightBox height={8} />
              <CustomTypography>Period</CustomTypography>
            </Stack>
            <HeightBox height={20} />
            <Stack direction="column" spacing={1}>
              <Autocomplete
                disablePortal
                onChange={(event, value) => {
                  setFormValues({
                    ...formValues,
                    prev_country: value?.label,
                  });
                }}
                value={formValues.prev_country}
                id="combo-box-demo"
                options={countries}
                renderInput={(params) => <CustomTextField {...params} />}
              />
              <Stack direction="row" spacing={1}>
                <CustomTextField
                  type="date"
                  size="small"
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      prev_contract_start: e.target.value,
                    })
                  }
                  value={formValues.prev_contract_start}
                />
                <CustomTextField
                  type="date"
                  size="small"
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      prev_contract_end: e.target.value,
                    })
                  }
                  value={formValues.prev_contract_end}
                />
              </Stack>
            </Stack>
            <HeightBox height={20} />
            <Stack direction="row" spacing={1}>
              <Box sx={{ display: "flex" }}>
                <FormControl
                  sx={{ m: 3 }}
                  component="fieldset"
                  variant="standard"
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.driving}
                          onChange={handleChange}
                          name="driving"
                        />
                      }
                      label="Driving"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.sewing}
                          onChange={handleChange}
                          name="sewing"
                        />
                      }
                      label="Sewing"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.baby_sitting}
                          onChange={handleChange}
                          name="baby_sitting"
                        />
                      }
                      label="baby Sitting"
                    />
                  </FormGroup>
                </FormControl>
                <FormControl
                  component="fieldset"
                  sx={{ m: 3 }}
                  variant="standard"
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.cleaning}
                          onChange={handleChange}
                          name="cleaning"
                        />
                      }
                      label="Cleaning"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.cooking}
                          onChange={handleChange}
                          name="cooking"
                        />
                      }
                      label="Cooking"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.washing}
                          onChange={handleChange}
                          name="washing"
                        />
                      }
                      label="Washing"
                    />
                  </FormGroup>
                </FormControl>
              </Box>
            </Stack>
          </Stack>
          {/* Languae */}
          <Stack direction="row" spacing={10}>
            <div>
              <HeightBox height={5} />
              <CustomTypography>Languages</CustomTypography>
            </div>
            <div>
              <HeightBox height={5} />
              <CustomTypography>English</CustomTypography>
            </div>
            <div>
              <HeightBox height={5} />
              <CustomTypography>Arabic</CustomTypography>
            </div>
            <div>
              <HeightBox height={5} />
              <CustomTypography>Hindi</CustomTypography>
            </div>
            <div>
              <HeightBox height={5} />
              <CustomTypography>Tamil</CustomTypography>
            </div>
            <CustomTextField
              size="small"
              placeholder="other"
              style={{ width: 150 }}
              value={formValues.other}
              onChange={(e) =>
                setFormValues({ ...formValues, other: e.target.value })
              }
            />
          </Stack>

          <Stack direction="row" spacing={5}>
            <div>
              <HeightBox height={5} />
              <CustomTypography>Fluency</CustomTypography>
            </div>
            <div></div>
            <div>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={formValues.english}
                onChange={(e) => {
                  setFormValues({ ...formValues, english: e.target.value });
                }}
              >
                <FormControlLabel value={0} control={<Radio />} label="Poor" />
                <FormControlLabel value={1} control={<Radio />} label="Fair" />
                <FormControlLabel
                  value={2}
                  control={<Radio />}
                  label="Fluent"
                />
              </RadioGroup>
            </div>
            <div>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={formValues.arabic}
                onChange={(e) => {
                  setFormValues({ ...formValues, arabic: e.target.value });
                }}
              >
                <FormControlLabel value={0} control={<Radio />} label="Poor" />
                <FormControlLabel value={1} control={<Radio />} label="Fair" />
                <FormControlLabel
                  value={2}
                  control={<Radio />}
                  label="Fluent"
                />
              </RadioGroup>
            </div>
            <div>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={formValues.hindi}
                onChange={(e) => {
                  setFormValues({ ...formValues, hindi: e.target.value });
                }}
              >
                <FormControlLabel value={0} control={<Radio />} label="Poor" />
                <FormControlLabel value={1} control={<Radio />} label="Fair" />
                <FormControlLabel
                  value={2}
                  control={<Radio />}
                  label="Fluent"
                />
              </RadioGroup>
            </div>
            <div>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={formValues.tamil}
                onChange={(e) => {
                  setFormValues({ ...formValues, tamil: e.target.value });
                }}
              >
                <FormControlLabel value={0} control={<Radio />} label="Poor" />
                <FormControlLabel value={1} control={<Radio />} label="Fair" />
                <FormControlLabel
                  value={2}
                  control={<Radio />}
                  label="Fluent"
                />
              </RadioGroup>
            </div>
            <div>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={formValues.otherLangFl}
                onChange={(e) => {
                  setFormValues({ ...formValues, otherLangFl: e.target.value });
                }}
              >
                <FormControlLabel value={0} control={<Radio />} label="Poor" />
                <FormControlLabel value={1} control={<Radio />} label="Fair" />
                <FormControlLabel
                  value={2}
                  control={<Radio />}
                  label="Fluent"
                />
              </RadioGroup>
            </div>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Stack direction="column" spacing={1}>
              <HeightBox height={2} />
              <CustomTypography>Post Applied for</CustomTypography>
              {/* {formik.touched.des_country && formik.errors.des_country && <HeightBox height={20} />} */}
              <HeightBox height={8} />
              <CustomTypography>Contract period</CustomTypography>
              {/* {formik.touched.height && formik.errors.height && <HeightBox height={20} />} */}
              <HeightBox height={8} />
              <CustomTypography>Monthly salary</CustomTypography>
            </Stack>
            <HeightBox height={20} />
            <Stack direction="column" spacing={1}>
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    post_applied_for: e.target.value,
                  })
                }
                value={formValues.post_applied_for}
              />
              <Stack direction="row" spacing={1}>
                <CustomTextField
                  type="date"
                  size="small"
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      contract_start: e.target.value,
                    })
                  }
                  value={formValues.contract_start}
                />
                <CustomTextField
                  type="date"
                  size="small"
                  onChange={(e) =>
                    setFormValues({
                      ...formValues,
                      contract_end: e.target.value,
                    })
                  }
                  value={formValues.contract_end}
                />
              </Stack>
              <CustomTextField
                size="small"
                onChange={(e) =>
                  setFormValues({ ...formValues, salary: e.target.value })
                }
                value={formValues.salary}
              />
              {/* {formik.touched.full_name && <ErrorMessage>{formik.errors.full_name}</ErrorMessage>} */}

              {/* {formik.touched.dob && <ErrorMessage>{formik.errors.dob}</ErrorMessage>} */}
              {/* <CustomTextField disabled size="small" onChange={formik.handleChange("age")} />
                  {formik.touched.age && <ErrorMessage>{formik.errors.age}</ErrorMessage>} */}

              {/* {formik.touched.nic && <ErrorMessage>{formik.errors.nic}</ErrorMessage>} */}
            </Stack>
            <HeightBox height={20} />
          </Stack>
          <HeightBox height={20} />
          <Stack direction="row" spacing={1}>
            <Stack direction="column" spacing={1}>
              <HeightBox height={20} />
              <CustomTypography>Passport size image</CustomTypography>
              <EmployeeProfileImage
                fileUploadHandler={selectPassportSizePhoto}
                photo={showImageVals.passportSize}
              />
            </Stack>
            <Stack direction="column" spacing={1}>
              <HeightBox height={20} />
              <CustomTypography>Full size image</CustomTypography>
              <EmployeeProfileImage
                fileUploadHandler={selectFullSizePhoto}
                photo={showImageVals.fullSize}
              />
            </Stack>
          </Stack>
          <HeightBox height={20} />
          <Stack direction="row" spacing={1}>
            <div style={{ width: 640 }} />
            <div>
              {/* <ReactToPrint trigger={() => <CustomButton>Print</CustomButton>} content={() => componentRef.current} /> */}
              <div style={{ display: "none" }}>
                <CustomerApplicationPrintOut
                  displayImages={showImageVals}
                  ref={componentRef}
                  formValues={formValues}
                />
              </div>
            </div>
            {!pageInfo.isDraft && pageInfo.isCreated && (
              <CustomButton type="button" onClick={handlePrint}>
                Print{" "}
              </CustomButton>
            )}
            {!pageInfo.isDraft && pageInfo.isCreated && (
              <CustomButton onClick={handleSubmit}>
                {loading ? <CircularProgress size={15} /> : "Submit"}{" "}
              </CustomButton>
            )}
            {!pageInfo.isDraft && !pageInfo.isCreated && (
              <CustomButton onClick={handleSubmit}>
                {loading ? <CircularProgress size={15} /> : " Submit & Print"}{" "}
              </CustomButton>
            )}
            {/* //only print if in draft */}
          </Stack>
        </form>
      )}
    </Container>
  );
}
